/**
 * Availability Engine - Handles date/time and availability logic
 * Separated from UI components for modularity and testability
 */

/**
 * Check if a date range is valid
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {object} Validation result
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate) {
    return { valid: false, error: 'Start date is required' };
  }

  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (isNaN(start.getTime())) {
    return { valid: false, error: 'Invalid start date format' };
  }

  if (start < today) {
    return { valid: false, error: 'Start date cannot be in the past' };
  }

  if (end) {
    if (isNaN(end.getTime())) {
      return { valid: false, error: 'Invalid end date format' };
    }

    if (end < start) {
      return { valid: false, error: 'End date must be after start date' };
    }
  }

  return { valid: true, start, end };
};

/**
 * Calculate duration between two dates
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {object} Duration breakdown
 */
export const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { days: 0, hours: 0, minutes: 0, totalMinutes: 0 };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { days: 0, hours: 0, minutes: 0, totalMinutes: 0 };
  }

  const diffMs = end - start;
  if (diffMs < 0) {
    return { days: 0, hours: 0, minutes: 0, totalMinutes: 0 };
  }

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  return { days, hours, minutes, totalMinutes };
};

/**
 * Check if a date falls within an available range
 * @param {string|Date} date - Date to check
 * @param {Array} availableRanges - Array of { start, end } objects
 * @returns {boolean}
 */
export const isDateAvailable = (date, availableRanges = []) => {
  if (!date || availableRanges.length === 0) return true;

  const checkDate = new Date(date);
  if (isNaN(checkDate.getTime())) return false;

  return availableRanges.some((range) => {
    const start = new Date(range.start);
    const end = new Date(range.end);
    return checkDate >= start && checkDate <= end;
  });
};

/**
 * Check for date conflicts (overlapping bookings)
 * @param {object} newBooking - { start, end }
 * @param {Array} existingBookings - Array of { start, end } objects
 * @returns {object} Conflict check result
 */
export const checkDateConflicts = (newBooking, existingBookings = []) => {
  if (!newBooking.start || !newBooking.end) {
    return { hasConflict: false, conflicts: [] };
  }

  const newStart = new Date(newBooking.start);
  const newEnd = new Date(newBooking.end);
  const conflicts = [];

  existingBookings.forEach((booking, index) => {
    const existingStart = new Date(booking.start);
    const existingEnd = new Date(booking.end);

    // Check for overlap
    if (newStart <= existingEnd && newEnd >= existingStart) {
      conflicts.push({ index, booking });
    }
  });

  return {
    hasConflict: conflicts.length > 0,
    conflicts,
  };
};

/**
 * Calculate rental pricing based on duration
 * @param {number} dailyRate - Daily rental rate
 * @param {number} weeklyRate - Weekly rate (optional, for discounts)
 * @param {number} monthlyRate - Monthly rate (optional, for discounts)
 * @param {number} days - Number of days
 * @returns {object} Pricing breakdown
 */
export const calculateRentalPrice = (dailyRate, weeklyRate, monthlyRate, days) => {
  if (!dailyRate || days <= 0) {
    return { total: 0, perDay: 0, discount: 0, appliedRate: 'daily' };
  }

  const daily = parseFloat(dailyRate);
  const weekly = parseFloat(weeklyRate) || daily * 7;
  const monthly = parseFloat(monthlyRate) || daily * 30;

  let total = 0;
  let appliedRate = 'daily';
  let effectivePerDay = daily;

  if (days >= 30 && monthlyRate) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    total = months * monthly + remainingDays * daily;
    appliedRate = 'monthly';
    effectivePerDay = total / days;
  } else if (days >= 7 && weeklyRate) {
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    total = weeks * weekly + remainingDays * daily;
    appliedRate = 'weekly';
    effectivePerDay = total / days;
  } else {
    total = days * daily;
    effectivePerDay = daily;
  }

  const standardTotal = days * daily;
  const discount = standardTotal > total ? standardTotal - total : 0;

  return {
    total: Math.round(total * 100) / 100,
    perDay: Math.round(effectivePerDay * 100) / 100,
    discount: Math.round(discount * 100) / 100,
    appliedRate,
    days,
  };
};

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {string} format - Format type: 'short', 'long', 'iso'
 * @returns {string}
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return '';

  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const options = {
    short: { day: '2-digit', month: 'short', year: 'numeric' },
    long: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
    iso: {},
  };

  if (format === 'iso') {
    return d.toISOString().split('T')[0];
  }

  return d.toLocaleDateString('en-IN', options[format] || options.short);
};

/**
 * Format time for display
 * @param {string} time - Time string (HH:MM format)
 * @returns {string} Formatted time (12-hour)
 */
export const formatTime = (time) => {
  if (!time) return '';

  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time;

  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;

  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Get relative time string
 * @param {string|Date} date - Date to compare
 * @returns {string} Relative time (e.g., "in 3 days", "2 hours ago")
 */
export const getRelativeTime = (date) => {
  if (!date) return '';

  const d = new Date(date);
  const now = new Date();
  const diffMs = d - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffDays > 0) {
    return diffDays === 1 ? 'Tomorrow' : `In ${diffDays} days`;
  } else if (diffDays < 0) {
    return diffDays === -1 ? 'Yesterday' : `${Math.abs(diffDays)} days ago`;
  } else if (diffHours > 0) {
    return `In ${diffHours} hours`;
  } else if (diffHours < 0) {
    return `${Math.abs(diffHours)} hours ago`;
  } else if (diffMinutes > 0) {
    return `In ${diffMinutes} minutes`;
  } else if (diffMinutes < 0) {
    return `${Math.abs(diffMinutes)} minutes ago`;
  } else {
    return 'Now';
  }
};

export default {
  validateDateRange,
  calculateDuration,
  isDateAvailable,
  checkDateConflicts,
  calculateRentalPrice,
  formatDate,
  formatTime,
  getRelativeTime,
};
